/**
 * Size of the onboarding iPhone (react-device-mockup frame, bezel included)
 * at IPhoneMockup's default `screenWidth` of 300, measured by rendering it
 * (see `IPHONE_FRAME_ASPECT` in `@/lib/app-editor/device-fit`). Every scene is
 * laid out for this size, so below `lg` the whole frame is scaled down as one
 * piece instead of being re-rendered at another `screenWidth`.
 */
export const MOCKUP_FRAME_SIZE = { width: 320, height: 664 } as const;

const MOCKUP_FIT_PADDING_PX = 16;

/** Scale that fits the mockup frame inside the canvas, never above 1. */
export function fitMockupScale(canvas: {
  width: number;
  height: number;
}): number {
  const scale = Math.min(
    1,
    (canvas.width - MOCKUP_FIT_PADDING_PX * 2) / MOCKUP_FRAME_SIZE.width,
    (canvas.height - MOCKUP_FIT_PADDING_PX * 2) / MOCKUP_FRAME_SIZE.height
  );
  return Math.max(0, scale);
}

/**
 * What a step frames in the mobile Studio: the point (`x`, `y`, fractions of
 * MOCKUP_FRAME_SIZE) that goes to the center of the canvas, and how much to
 * magnify, as a multiple of the scale at which the frame fills the canvas
 * width (1 = phone edge to edge).
 */
export interface MockupFocus {
  x: number;
  y: number;
  zoom: number;
}

/**
 * The new app's icon and name on the home screen (HomeScreenScene's ICON_SLOT,
 * measured in the frame at x 0.069–0.278, y 0.336–0.435). Centered a bit to
 * the right of it, so the phone's left edge stays in view.
 */
export const HOME_ICON_FOCUS: MockupFocus = { x: 0.25, y: 0.385, zoom: 1.6 };

/**
 * The tab bar, phone edge to edge, at the same point the notification step
 * starts from (its SHARED_ZOOM_ORIGIN_Y, 92% down the frame), so going from
 * one step to the other does not move the phone.
 */
export const TAB_BAR_FOCUS: MockupFocus = { x: 0.5, y: 0.92, zoom: 1 };

/**
 * Share of the screen the form's sheet opens at, the same on every step: a
 * different height from one step to the next would make the page transition
 * crossfade two Studios of different sizes. Enough for the title and the
 * first fields; the Studio keeps a band for what each step frames.
 */
export const STUDIO_SHEET_RATIO = 0.74;

/** Scale and top-left position of the mockup frame inside the canvas. */
export interface MockupPlacement {
  scale: number;
  x: number;
  y: number;
}

/**
 * The whole phone fitted and centered, or, with a `focus`, magnified around
 * that point placed at the center of the canvas (the canvas clips the rest).
 */
export function placeMockup(
  canvas: { width: number; height: number },
  focus?: MockupFocus
): MockupPlacement {
  if (!focus) {
    const scale = fitMockupScale(canvas);
    return {
      scale,
      x: (canvas.width - MOCKUP_FRAME_SIZE.width * scale) / 2,
      y: (canvas.height - MOCKUP_FRAME_SIZE.height * scale) / 2,
    };
  }
  const scale = (canvas.width / MOCKUP_FRAME_SIZE.width) * focus.zoom;
  return {
    scale,
    x: canvas.width / 2 - focus.x * MOCKUP_FRAME_SIZE.width * scale,
    y: canvas.height / 2 - focus.y * MOCKUP_FRAME_SIZE.height * scale,
  };
}

/**
 * Smaller differences between the visual and the layout viewport are Safari's
 * toolbars collapsing, not the keyboard (the iPhone keyboard is ~290pt or more).
 */
const KEYBOARD_MIN_HEIGHT_PX = 150;

/**
 * Whether the on-screen keyboard is open. iOS Safari shrinks the visual
 * viewport (and `window.innerHeight`) for the keyboard, but not the layout
 * viewport (`document.documentElement.clientHeight`).
 */
export function isKeyboardOpen({
  visualHeight,
  layoutHeight,
}: {
  visualHeight: number;
  layoutHeight: number;
}): boolean {
  return layoutHeight - visualHeight > KEYBOARD_MIN_HEIGHT_PX;
}
